import { z } from 'zod';
import { DeviceStatus, DeviceType, CommandType } from '../types/device.js';
import { SensorTypeSchema } from './sensor.js';
// ---------------------------------------------------------------------------
// 기본 상수 스키마
// ---------------------------------------------------------------------------
export const DeviceStatusSchema = z.enum([
    DeviceStatus.ACTIVE,
    DeviceStatus.WARNING,
    DeviceStatus.ERROR,
    DeviceStatus.OFFLINE,
]);
export const DeviceTypeSchema = z.enum([
    DeviceType.SENSOR_NODE,
    DeviceType.GATEWAY,
    DeviceType.ACTUATOR,
]);
export const CommandTypeSchema = z.enum([
    CommandType.REBOOT,
    CommandType.OTA_UPDATE,
    CommandType.SET_INTERVAL,
    CommandType.ACTUATE,
    CommandType.REPORT,
    CommandType.CAPTURE,
]);
// ---------------------------------------------------------------------------
// 위치 스키마
// ---------------------------------------------------------------------------
export const GeoLocationSchema = z.object({
    lat: z.number().min(-90).max(90),
    lng: z.number().min(-180).max(180),
    altitude: z.number().optional(),
    description: z.string().max(200).optional(),
    address: z.string().max(200).optional(),
});
// ---------------------------------------------------------------------------
// 디바이스 정보 스키마
// ---------------------------------------------------------------------------
export const DeviceInfoSchema = z.object({
    id: z.string().min(1, '장비 ID는 비어 있을 수 없습니다'),
    name: z.string().min(1).max(100),
    type: DeviceTypeSchema,
    location: GeoLocationSchema.optional(),
    installedAt: z.string().date(),
    firmwareVersion: z
        .string()
        .regex(/^\d+\.\d+\.\d+$/, '펌웨어 버전은 semver 형식(x.y.z)이어야 합니다'),
    status: DeviceStatusSchema,
    sensors: z.array(SensorTypeSchema).min(1, '센서가 1개 이상 연결되어야 합니다'),
    lastSeenAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    mqttApiKey: z.string().optional(),
    cropType: z.string().max(100).optional(),
    deviceModelId: z.string().optional(),
    farmId: z.string().uuid().nullable().optional(),
    model: z
        .union([z.literal(1), z.literal(2)])
        .nullable()
        .optional(),
    parentDeviceId: z.string().nullable().optional(),
    slaveAddr: z.number().int().min(1).max(247).nullable().optional(),
});
// ---------------------------------------------------------------------------
// 디바이스 등록/수정 요청 스키마
// ---------------------------------------------------------------------------
export const CreateDeviceSchema = DeviceInfoSchema.omit({
    id: true,
    status: true,
    lastSeenAt: true,
    createdAt: true,
    updatedAt: true,
    mqttApiKey: true,
}).extend({
    /** 장비 ID 접두어로 사용할 모델 코드 (서버 채번용) */
    modelCode: z
        .string()
        .regex(/^[A-Z0-9]{2,6}$/, '모델 코드는 대문자 영숫자 2~6자여야 합니다'),
});
export const UpdateDeviceSchema = CreateDeviceSchema.partial();
// ---------------------------------------------------------------------------
// 기기 등록(사용자 앱) — 하드웨어 ID로 농장에 장비를 등록한다.
// 센서/펌웨어 등 상세는 장비가 MQTT로 보고하므로 등록 시점엔 ID·별칭·농장만 받는다.
// ---------------------------------------------------------------------------
export const RegisterDeviceSchema = z.object({
    /** 장비 본체에 표기된 하드웨어 ID (devices.id, TEXT) */
    id: z.string().min(1).max(64),
    /** 표시 별칭 (미지정 시 ID 사용) */
    name: z.string().min(1).max(100).optional(),
    /** 소속 농장 ID — 위치/날씨/AI 기준 */
    farmId: z.string().uuid(),
});
// ---------------------------------------------------------------------------
// RS485 하위 노드(Slave) 등록 — 마스터 아래에 슬레이브를 devices row로 추가한다.
// 센서 목록·유형은 모델(code)에서 서버가 도출하므로 주소·모델·별칭만 받는다.
// ---------------------------------------------------------------------------
export const RegisterNodeSchema = z.object({
    /** RS485 슬레이브 주소 (1~247) */
    slaveAddr: z.number().int().min(1).max(247),
    /** 매핑할 장비모델 code (단위·검증범위 도출 기준) */
    deviceModelId: z
        .string()
        .regex(/^[A-Z0-9]{2,6}$/, '모델 코드는 대문자 영숫자 2~6자여야 합니다'),
    /** 표시 별칭 (미지정 시 자동 생성) */
    name: z.string().min(1).max(100).optional(),
});
// ---------------------------------------------------------------------------
// MQTT 상태 보고 페이로드 스키마
// ---------------------------------------------------------------------------
export const DeviceStatusPayloadSchema = z.object({
    deviceId: z.string().min(1),
    status: DeviceStatusSchema,
    firmwareVersion: z.string().regex(/^\d+\.\d+\.\d+$/),
    uptimeSeconds: z.number().int().nonnegative(),
    timestamp: z.string().datetime(),
    /** 장비 모델 등급(펌웨어 보고) — 1: 환경 센서만, 2: 센서 + 생육 사진/측정 */
    model: z
        .union([z.literal(1), z.literal(2)])
        .nullable()
        .optional(),
});
// ---------------------------------------------------------------------------
// MQTT 제어 명령 페이로드 스키마
// ---------------------------------------------------------------------------
export const DeviceCommandPayloadSchema = z.object({
    commandId: z.string().uuid(),
    deviceId: z.string().min(1),
    type: CommandTypeSchema,
    params: z.record(z.unknown()).optional(),
    issuedAt: z.string().datetime(),
});
//# sourceMappingURL=device.js.map